import { AnimatePresence } from 'framer-motion';
import { useMemo, type ReactNode } from 'react';
import { AppLoader } from '@/components/AppLoader';
import { FadeInOut } from '@/components/FadeInOut';
import { useCurrentUserContext } from '@/contexts';
import { CollectionContextProvider } from '@/features/events/contexts/CollectionContext/CollectionContextProvider';
import { getExperienceInteractionApps } from '@/features/events/lib/createDecoratedExperienceEvent';
import { isExperienceEvent } from '@/features/events/lib/event.lib';
import type { DecoratedExperienceEvent } from '@/features/events/types';
import {
  PageExperienceContext,
  usePageExperienceContext,
} from '@/features/experiences/contexts/PageExperienceContext';
import {
  decorateChild,
  inheritInteractionApps,
} from '@/features/planner/child.lib';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { Center, Spinner, Text } from '@/ui';
import { useGetPageExperience } from '../hooks/useGetPageExperience';
import { useGetPublishedChildExperience } from '../hooks/useGetPublishedChildExperience';
import { remapPlannerCalendarExperience } from '../lib/plannerCalendarCompat';
import { isNotFound } from '../lib/queryError';
import NotFound from '../pages/NotFound';
import { ErrorState } from './ErrorState';

type Props = {
  /**
   * Whether the active view's `preview` rides along — required, because the
   * answer follows from what this subtree is for rather than from any default.
   * The page renders the preview; the customize sidebar loads the same child to
   * render its CONTROLS, which must keep reporting the committed value while
   * the page shows the hovered one, so it opts out. (The schedule-wide
   * `parentPreview` is unaffected either way: it reaches children through the
   * graft below, the same route their committed values take.)
   */
  applyPreview: boolean;
  childExperienceId: string;
  children: ReactNode;
  /**
   * Overrides the shared context events, e.g. to render the full event set
   * while the page-level query is bounded by the URL date range.
   */
  events?: DecoratedExperienceEvent[];
  /**
   * Overrides the inherited reveal mode. The print modal passes false: what
   * prints must be what was saved, whatever `?hidden=show` is doing to the page
   * this subtree loads from.
   */
  showHiddenEvents?: boolean;
  /**
   * 'page' (default): full-viewport loading/error fallbacks suited for page-level rendering.
   * 'inline': compact fallbacks suited for embedding inside a panel or sidebar rail.
   */
  variant?: 'page' | 'inline';
};

export const ChildExperienceLoader = ({
  applyPreview,
  childExperienceId,
  children,
  events,
  showHiddenEvents,
  variant = 'page',
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const context = usePageExperienceContext();
  const { isPublishedView, isChild } = context;
  const { value: printableBlockEnabled } = useFeatureFlag(
    'printableBlockEnabled'
  );
  const { value: interactionInheritEnabled } = useFeatureFlag(
    'interactionInheritEnabled'
  );

  // On the published view the viewer may be anonymous, so children load
  // through the public parent-slug query instead of the authed getExperience.
  // Keyed on isPublishedView rather than readOnly: an authed user whose
  // explicit role only grants viewing is read-only too, but loads through the
  // authed pipeline (and the planner may not even be published). In
  // child-scoped contexts (e.g. the print modal) the parent planner — the
  // slug owner — sits on parentExperience.
  const parentPlanner = isChild ? context.parentExperience : context.experience;
  const parentSlug = parentPlanner?.publishedSlug ?? undefined;

  const liveQuery = useGetPageExperience(
    isPublishedView ? undefined : childExperienceId
  );
  const publishedQuery = useGetPublishedChildExperience(
    isPublishedView ? parentSlug : undefined,
    isPublishedView ? childExperienceId : undefined
  );
  const {
    data: experience,
    isPending,
    error,
  } = isPublishedView ? publishedQuery : liveQuery;

  // Destructure the specific parent fields the decoration reads so the memo
  // below depends on their values, not the parent object's identity — the edit
  // provider spreads a fresh `context.experience` on every render while a style
  // preview is active, which would otherwise re-decorate the child each frame.
  const {
    feeds: parentFeeds,
    categories: parentCategories,
    blocks: parentBlocks,
    interactionApps: parentInteractionApps,
    interactionSlots: parentInteractionSlots,
    colorType,
    customHue,
    customSaturation,
    customLightness,
    customAlpha,
    hue,
    saturation,
    lightness,
    colorPalette,
    colorMode,
    primaryColor,
    vibe,
    accentBackgroundEnabled,
    bold,
    logoImageUrl,
    timeZone,
    timeZoneDisplay,
  } = context.experience;

  const decoratedExperience = useMemo(() => {
    if (!experience) {
      return undefined;
    }

    const base = currentUser
      ? decorateChild(experience, {
          feeds: parentFeeds,
          owner: currentUser,
          categories: parentCategories,
          parentColors: {
            colorType,
            customHue,
            customSaturation,
            customLightness,
            customAlpha,
            hue,
            saturation,
            lightness,
          },
        })
      : experience;
    // Palette/color lives on the parent planner (the style editor writes to the
    // parent). Graft it regardless of auth so anonymous published views and
    // iframe-rendered printables (which receive the experience as a prop, outside
    // this context) resolve the parent's color live. decorateChild already stamps
    // it for the authed path; grafting here covers the anonymous path (where
    // decorateChild is skipped). The Style panel's accent background travels with it,
    // for the same reason: the page canvas has to be the one the author set on the
    // schedule, not whatever a freshly created child defaulted to. The Display-section
    // toggles are per-view, so they are intentionally NOT grafted — each child keeps
    // its own, logoDisplay included: the planner owns the image, the view decides
    // whether to draw it.
    //
    // Both time-zone fields travel with them, for a sharper version of the same
    // reason: they are a schedule-wide event setting (Schedule settings' "Time
    // zone" panel writes them to the parent) and no UI can reach a child's own
    // pair, so a child keeps whatever it was seeded with at creation forever. Left
    // ungrafted, an author who moves zones updates the planner while every view
    // goes on resolving `getDisplayTimezone` against the zone it was born in — the
    // window it renders, which day it calls today, and the times on any events a
    // child-scoped consumer re-decorates. Grafting makes the parent's pair the one
    // every surface resolves, which is also the only one the author can edit.
    const childExperience = remapPlannerCalendarExperience(
      {
        ...base,
        isPlannerChild: true,
        colorPalette,
        colorMode,
        primaryColor,
        vibe,
        accentBackgroundEnabled,
        bold,
        logoImageUrl,
        timeZone,
        timeZoneDisplay,
      },
      !!printableBlockEnabled
    );

    // Interaction apps cascade the same way, and for the same auth-independent
    // reason: an anonymous visitor is exactly who uses add-to-calendar. They are
    // inherited OUTSIDE the remap because the remap restamps `blocks` wholesale,
    // which would orphan slots pointed at the pre-remap collection block.
    //
    // Which apps come down is the schedule's own decision now (the Send-to-
    // calendar panel's "Include controls in page"). The flag stays as the
    // migration switch it was built to be: while it is on the whole cascade
    // happens as it shipped, so flipping it off is what hands the choice to the
    // author.
    return inheritInteractionApps(
      childExperience,
      {
        blocks: parentBlocks,
        interactionApps: parentInteractionApps,
        interactionSlots: parentInteractionSlots,
      },
      { inheritAll: !!interactionInheritEnabled }
    );
  }, [
    experience,
    currentUser,
    printableBlockEnabled,
    interactionInheritEnabled,
    parentFeeds,
    parentCategories,
    parentBlocks,
    parentInteractionApps,
    parentInteractionSlots,
    colorType,
    customHue,
    customSaturation,
    customLightness,
    customAlpha,
    hue,
    saturation,
    lightness,
    colorPalette,
    colorMode,
    primaryColor,
    vibe,
    accentBackgroundEnabled,
    bold,
    logoImageUrl,
    timeZone,
    timeZoneDisplay,
  ]);

  // The view preview belongs to whichever child is loaded here: a planner
  // never applies it to itself (it is chrome around the view), and this loader
  // replaces `experience` wholesale with the fetched child, so this is the one
  // place it lands. Merging it here means a previewed layout or a hovered
  // customize control rides the exact same render path as the committed child
  // and can't diverge from what a click would produce.
  const preview = applyPreview ? context.preview : null;
  const previewedExperience = useMemo(
    () =>
      decoratedExperience && preview
        ? { ...decoratedExperience, ...preview }
        : decoratedExperience,
    [decoratedExperience, preview]
  );

  // The shared events are decorated against the parent experience, whose
  // interaction slots differ from this child's. Re-derive each event's
  // interaction apps from the child experience so per-card controls (e.g.
  // add-to-calendar) reflect the child's own configuration.
  // With no explicit list, the reveal mode decides which of the context's two
  // sets to inherit. Taking `events` while overriding the mode to true would ask
  // for hidden occurrences from a list the parent had already filtered them out
  // of — the override would silently do nothing.
  const baseEvents =
    events ??
    (showHiddenEvents && !context.isShowingHiddenEvents
      ? context.allEvents
      : context.events);
  const childEvents = useMemo(() => {
    if (!decoratedExperience) {
      return baseEvents;
    }
    const interactionApps = getExperienceInteractionApps(decoratedExperience);
    return baseEvents.map((event) =>
      isExperienceEvent(event) ? { ...event, interactionApps } : event
    );
  }, [baseEvents, decoratedExperience]);

  return (
    /*
      Every branch renders with initial="show": these subtrees lazy-load chunks
      (the layout, and the printable template renderer — which re-resolves when
      the printableBlockEnabled flag arrives) that re-suspend an ancestor
      boundary, cancelling an in-flight enter animation and stranding the branch
      at opacity 0, i.e. a blank page. Only exits animate now; a branch swap is
      a fade-out followed by an instant appear, which can never strand.
    */
    <AnimatePresence mode="wait">
      {previewedExperience ? (
        <FadeInOut initial="show" key="loaded">
          <PageExperienceContext.Provider
            value={{
              ...context,
              experience: previewedExperience,
              // The owning planner, not whatever experience happens to be in
              // scope: the loader nests (the print modal reloads the active
              // child from inside that child's own context), and taking
              // context.experience there would make a child its own parent —
              // so an inherited header title would resolve to the child's own
              // title instead of the planner's.
              parentExperience: context.parentExperience ?? context.experience,
              events: childEvents,
              isShowingHiddenEvents:
                showHiddenEvents ?? context.isShowingHiddenEvents,
              isChild: true,
              // Kept in step with what was actually applied, so nothing
              // downstream reads a preview this subtree is not rendering.
              preview,
            }}
          >
            <CollectionContextProvider
              base={{ experience: context.experience }}
              events={childEvents}
            >
              {children}
            </CollectionContextProvider>
          </PageExperienceContext.Provider>
        </FadeInOut>
      ) : error && !isPending ? (
        <FadeInOut initial="show" key="no-experience">
          {variant === 'inline' ? (
            <Center h={20} w="100%">
              <Text color="ink.muted" fontSize="ah-label">
                {isNotFound(error) ? 'View not found.' : 'Failed to load view.'}
              </Text>
            </Center>
          ) : isNotFound(error) ? (
            <NotFound />
          ) : (
            <ErrorState />
          )}
        </FadeInOut>
      ) : (
        <FadeInOut initial="show" key="loading">
          {variant === 'inline' ? (
            <Center h={20} w="100%">
              <Spinner color="ink.muted" size="sm" />
            </Center>
          ) : (
            <Center h="100vh" maxW="100%" w="100vw">
              <AppLoader />
            </Center>
          )}
        </FadeInOut>
      )}
    </AnimatePresence>
  );
};
